import React from 'react';
import { formatCurrency, formatNumber } from '../lib/format';
import { useT } from '../lib/i18n';

// Calum palette (gold / green / mint) — Perfy structure, Calum colours.
export const CALUM = {
  gold: '#cf9a40',
  green: '#114817',
  mint: '#8bd4be',
  sand: '#e7d3ab',
  clay: '#b5651d',
  slate: '#64748b',
};
export const SERIES = [CALUM.gold, CALUM.green, CALUM.mint, CALUM.clay, CALUM.slate, CALUM.sand];

/** Section card — the standard white panel used across the dashboard. */
export const SectionCard = ({ title, subtitle, right, children, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2">
        <div>
          {title && <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>{title}</h3>}
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    <div className="px-4 pb-4">{children}</div>
  </div>
);

/** KPI tile — label, big value, optional sub + accent bar.
 *  `delta` (a % vs the comparison) renders as a green/red arrow line above `sub`;
 *  `delta2` adds a second, quieter one (e.g. "so far today" vs "the whole day").
 *  With `onClick` the whole tile becomes a button (drill-down to the receipts). */
export const KpiTile = ({ label, value, sub, delta, deltaLabel, delta2, delta2Label,
  accent = 'gold', icon: Icon, onClick, actionLabel }) => {
  const bar = { gold: 'bg-[#cf9a40]', green: 'bg-[#114817]', mint: 'bg-[#8bd4be]', slate: 'bg-slate-400' }[accent] || 'bg-[#cf9a40]';
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag {...(onClick ? { type: 'button', onClick, title: actionLabel, 'aria-label': actionLabel ? `${label} — ${actionLabel}` : label } : {})}
      className={`relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm p-4 ${
        onClick ? 'text-left w-full cursor-pointer transition-colors hover:border-[#cf9a40] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cf9a40]' : ''}`}>
      <div className={`absolute left-0 top-0 h-full w-1 ${bar}`} />
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-300" />}
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-900 tabular-nums leading-tight" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>{value}</div>
      {delta != null && (
        <div className="text-xs mt-0.5 tabular-nums">
          <span className={`font-semibold ${delta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {delta >= 0 ? '▲' : '▼'} {delta >= 0 ? '+' : ''}{String(delta).replace('.', ',')}%
          </span>
          {deltaLabel && <span className="text-slate-400"> {deltaLabel}</span>}
        </div>
      )}
      {delta2 != null && (
        <div className="text-[11px] mt-0.5 tabular-nums">
          <span className={delta2 >= 0 ? 'text-emerald-600/70' : 'text-red-500/70'}>
            {delta2 >= 0 ? '+' : ''}{String(delta2).replace('.', ',')}%
          </span>
          {delta2Label && <span className="text-slate-300"> {delta2Label}</span>}
        </div>
      )}
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      {onClick && actionLabel && (
        <div className="text-[10px] text-slate-300 mt-1 group-hover:text-[#cf9a40]">{actionLabel} →</div>
      )}
    </Tag>
  );
};

/** Shared tooltip — formats currency or number depending on series key.
 *  `deltas` maps a series key to the data key holding last year's value
 *  (e.g. { revenue: 'prev_revenue' }); matching rows get a green/red % vs LY. */
export const ChartTooltip = ({ active, payload, label, money, deltas }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const deltaFor = (key) => {
    const prev = deltas?.[key] != null ? row[deltas[key]] : null;
    const cur = row[key];
    if (!prev || cur == null) return null;
    return Math.round(1000 * (cur / prev - 1)) / 10;
  };
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((e, i) => {
        const d = deltaFor(e.dataKey);
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
            <span className="text-slate-600">{e.name}:</span>
            <span className="font-medium tabular-nums">
              {money?.includes?.(e.dataKey) ? formatCurrency(e.value) : formatNumber(e.value)}
            </span>
            {d != null && (
              <span className={`font-medium tabular-nums ${d >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {d >= 0 ? '+' : ''}{String(d).replace('.', ',')}%
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Pill toggle (e.g. Units / Revenue, or D/W/M). Touch-sized on phones. */
export const PillToggle = ({ value, onChange, options }) => (
  <div className="inline-flex flex-wrap items-center bg-slate-100 rounded-md p-0.5 text-xs sm:text-[11px] font-medium">
    {options.map((o) => (
      <button key={o.value} onClick={() => onChange(o.value)}
        className={`px-3 py-2 sm:px-2.5 sm:py-1 rounded transition-colors ${
          value === o.value ? 'bg-white text-[#114817] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

/** Text/search input class: 16px on phones (no iOS zoom), compact on desktop. */
export const INPUT = 'rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[16px] sm:text-xs sm:py-1.5 outline-none focus:border-[#cf9a40]';

/** Page header row that stacks on phones: title block left, controls right. */
export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
    <div className="min-w-0">
      <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>{title}</h1>
      {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2 max-w-full">{children}</div>}
  </div>
);

export const Loader = ({ label }) => {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[#cf9a40]" />
      <div className="mt-3 text-xs">{label ?? t('Laden…')}</div>
    </div>
  );
};

export const ErrorBox = ({ error, onRetry }) => {
  const t = useT();
  const detail = error?.response?.data?.detail;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <span>{t('Er ging iets mis bij het laden.')}{error?.response?.status ? ` (${error.response.status})` : ''}{detail ? ` — ${detail}` : ''}</span>
      {onRetry && (
        <button onClick={onRetry} className="rounded-md bg-white border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
          {t('Opnieuw proberen')}
        </button>
      )}
    </div>
  );
};

export const EmptyNote = ({ children }) => {
  const t = useT();
  return <p className="py-6 text-center text-sm text-slate-400">{children ?? t('Geen gegevens voor deze periode.')}</p>;
};

/**
 * Per-card data state. `q` is a useApi() result. Renders a compact loader,
 * an error box with retry, an empty note (when `isEmpty(data)` is true), or
 * the children (a render function receiving data, or plain elements).
 */
export const QueryState = ({ q, isEmpty, empty, children }) => {
  if (q?.loading && !q?.data) return <Loader />;
  if (q?.error) return <ErrorBox error={q.error} onRetry={q.reload} />;
  if (!q?.data || (isEmpty && isEmpty(q.data))) return <EmptyNote>{empty}</EmptyNote>;
  return typeof children === 'function' ? children(q.data) : children;
};
